// Folder DTO + closed enums (plan.md §VI). Mirrors the row produced by
// `packages/db/src/schema/content/folders.ts`. The wire shape is what
// the SPA renders in folder lists / breadcrumbs / move pickers; it
// excludes the materialized `path` column on routes that already
// expose it implicitly via the path segments (the SPA computes
// breadcrumbs from a separate `ancestors` array).
import { z } from 'zod';
import { FileAvailabilityState, FileGroupScope } from './scope.js';
/** Closed enum — keep in sync with `files_partition_check` and
 *  `folders_partition_check` in migration 0020. */
export const FilePartition = z.enum([
    'article-assets',
    'public-downloads',
    'private',
]);
/** Filename / folder-name shape: 1–255 chars, no `/`, no control chars,
 *  not '.' or '..'. The DB CHECK enforces this; the schema layer
 *  catches malformed input before it hits the wire. */
export const FsName = z
    .string()
    .min(1)
    .max(255)
    // Forbid path separators + control chars (U+0000–U+001F). The
    // `no-control-regex` rule flags the literal range, so we use \cX
    // notation: \c@ = U+0000, \c_ = U+001F.
    // eslint-disable-next-line no-control-regex
    .refine((v) => !/[/\x00-\x1f]/.test(v), 'name has invalid characters')
    .refine((v) => v !== '.' && v !== '..', 'name is reserved');
/** Personal tag — lowercase letters, digits, hyphens. Mirrors the
 *  bookmark-personal-tag shape. ≤ 64 chars; ≤ 32 per resource. */
export const FsTag = z.string().min(1).max(64);
export const FsTags = z.array(FsTag).max(32);
export const Folder = z.object({
    id: z.string().uuid(),
    ownerId: z.string().uuid(),
    /** Parent folder id; null when the folder sits at the partition root. */
    parentId: z.string().uuid().nullable(),
    partition: FilePartition,
    name: FsName,
    /** Materialized path (e.g. `/projects/2026/`). Always begins and
     *  ends with `/`; root partition is implicit and is NOT a folder
     *  row — root listing returns folders with parentId=null. */
    path: z.string().min(1).max(4096),
    description: z.string().max(2000).nullable(),
    tags: FsTags,
    /** ISO 8601 — non-null when the folder is in the recycle bin. */
    deletedAt: z.string().datetime({ offset: true }).nullable(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
    /** Aggregated child folder count at THIS level (for empty-state /
     *  badge rendering). Computed live by the route layer. */
    childFolderCount: z.number().int().nonnegative(),
    /** Aggregated child file count at THIS level. */
    childFileCount: z.number().int().nonnegative(),
    groupScopes: z.array(FileGroupScope),
    availabilityState: FileAvailabilityState,
    nextAvailableAt: z.string().datetime({ offset: true }).nullable(),
    expiresAt: z.string().datetime({ offset: true }).nullable(),
});
/** A folder ancestor entry — used to render breadcrumbs without a
 *  follow-up fetch per segment. The list is ordered root → leaf. */
export const FolderAncestor = z.object({
    id: z.string().uuid(),
    name: z.string(),
});
/** Limits enforced at every routing surface. The schema CHECKs are
 *  the authoritative answer; these constants are convenient for
 *  client-side eager validation. */
export const MAX_FOLDER_DEPTH = 32;
export const MAX_FOLDER_DESCRIPTION_LENGTH = 2000;
//# sourceMappingURL=folder.js.map